import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setAdminToken, getAdminToken } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { LogOut, Trash2, Send, ArrowLeft } from "lucide-react";
import { CEMETERIES, fmtDate, fmtTime, peso } from "@/data/tombBlessing";

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("inquiries");
  const navigate = useNavigate();

  const tryLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/login", { password });
      setAdminToken(res.data.token);
      setAuthed(true);
      toast.success("Welcome, parish admin.");
    } catch {
      toast.error("Incorrect password.");
    }
  };

  const logout = () => {
    setAdminToken(null);
    setAuthed(false);
    setPassword("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-parchment-100 flex items-center justify-center px-6">
        <form
          onSubmit={tryLogin}
          className="w-full max-w-md bg-parchment-50 border border-parchment-400 p-10"
        >
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-cocoa-700 mb-8 hover:text-terracotta transition-colors"
          >
            <ArrowLeft size={14} /> Back to parish
          </button>

          <div className="flex items-center gap-3 mb-2">
            <span className="text-gold text-xl">✦</span>
            <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark">
              Parish Administration
            </div>
          </div>
          <h1 className="font-serif text-4xl text-cocoa-900 leading-tight">
            Admin Sign In
          </h1>
          <p className="mt-3 font-sans text-cocoa-700 text-sm">
            Access inquiries, announcements, and construction progress.
          </p>

          <div className="mt-8">
            <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-1">
              Password
            </label>
            <input
              type="password"
              data-testid={ADMIN.loginPasswordInput}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-3 font-sans text-cocoa-900 focus:outline-none transition-colors"
              autoFocus
            />
          </div>

          <button
            type="submit"
            data-testid={ADMIN.loginSubmit}
            className="mt-8 w-full bg-cocoa-900 text-parchment-100 px-8 py-4 font-sans text-sm tracking-wide hover:bg-terracotta transition-colors"
          >
            Sign In →
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment-100">
      <header className="border-b border-parchment-400 bg-parchment-50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-gold text-xl">✦</span>
            <div>
              <div className="font-serif text-cocoa-900">Parish Admin</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-cocoa-700">
                Saint Francis of Assisi · Mabuhay
              </div>
            </div>
          </Link>

          <button
            data-testid={ADMIN.logoutButton}
            onClick={logout}
            className="flex items-center gap-2 text-sm font-sans text-cocoa-800 hover:text-terracotta transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="border-b border-parchment-400 bg-parchment-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex gap-8 overflow-x-auto">
          {[
            ["inquiries", "Inquiries", ADMIN.tabInquiries],
            ["appointments", "Appointments", "admin-tab-appointments"],
            ["tombs", "Tomb Blessings", "admin-tab-tombs"],
            ["intentions", "Intentions", "admin-tab-intentions"],
            ["pledges", "Pledges", "admin-tab-pledges"],
            ["announcements", "Announcements", ADMIN.tabAnnouncements],
            ["construction", "Construction", ADMIN.tabConstruction],
          ].map(([id, label, testId]) => (
            <button
              key={id}
              data-testid={testId}
              onClick={() => setTab(id)}
              className={`relative py-5 text-[11px] uppercase tracking-[0.28em] font-sans transition-colors whitespace-nowrap ${
                tab === id ? "text-terracotta" : "text-cocoa-700 hover:text-cocoa-900"
              }`}
            >
              {label}
              {tab === id && (
                <span className="absolute bottom-0 left-0 right-0 h-px bg-terracotta" />
              )}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {tab === "inquiries" && <InquiriesTab />}
        {tab === "appointments" && <AppointmentsTab />}
        {tab === "tombs" && <TombBlessingsTab />}
        {tab === "intentions" && <IntentionsTab />}
        {tab === "pledges" && <PledgesTab />}
        {tab === "announcements" && <AnnouncementsTab />}
        {tab === "construction" && <ConstructionTab />}
      </main>
    </div>
  );
}

function InquiriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/inquiries");
      setItems(res.data);
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">
            Parish Inquiries
          </div>
          <h2 className="font-serif text-3xl text-cocoa-900">
            {items.length} message{items.length === 1 ? "" : "s"}
          </h2>
        </div>
      </div>

      {loading ? (
        <div className="text-cocoa-700 font-sans">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-parchment-400 p-16 text-center text-cocoa-700 font-sans">
          ✦ No inquiries yet.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((i) => (
            <article
              key={i.id}
              data-testid={ADMIN.inquiryRow}
              className="bg-parchment-50 border border-parchment-400 p-6 md:p-8"
            >
              <div className="grid md:grid-cols-12 gap-4 md:gap-6">
                <div className="md:col-span-3">
                  <div className="font-serif text-xl text-cocoa-900">{i.name}</div>
                  <a href={`mailto:${i.email}`} className="text-sm text-cocoa-700 hover:text-terracotta">
                    {i.email}
                  </a>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mt-2">
                    {new Date(i.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="md:col-span-6">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-terracotta mb-1">
                    {i.concern}
                  </div>
                  <p className="font-sans text-cocoa-800 leading-relaxed font-light whitespace-pre-wrap">
                    {i.message}
                  </p>
                </div>

                <div className="md:col-span-3 flex md:flex-col gap-3 md:items-end justify-end">
                  <select
                    data-testid={ADMIN.inquiryStatusSelect}
                    value={i.status}
                    onChange={(e) => updateStatus(i.id, e.target.value)}
                    className="bg-parchment-100 border border-parchment-400 px-3 py-2 text-sm font-sans text-cocoa-900 focus:border-terracotta focus:outline-none"
                  >
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <button
                    data-testid={ADMIN.inquiryDelete}
                    onClick={() => remove(i.id)}
                    className="flex items-center gap-2 text-sm text-cocoa-700 hover:text-terracotta transition-colors"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ badge: "Formation", title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/announcements");
      setItems(res.data);
    } catch {
      toast.error("Failed to load");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body || !form.badge) return;
    setSubmitting(true);
    try {
      await api.post("/announcements", form);
      setForm({ badge: "Formation", title: "", body: "" });
      toast.success("Announcement posted.");
      load();
    } catch {
      toast.error("Could not post");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">
          Post a new
        </div>
        <h2 className="font-serif text-3xl text-cocoa-900 mb-8">Announcement</h2>

        <form onSubmit={create} className="bg-parchment-50 border border-parchment-400 p-8 space-y-5">
          <SmallField label="Badge">
            <input
              data-testid={ADMIN.announcementBadgeInput}
              value={form.badge}
              onChange={(e) => setForm({ ...form, badge: e.target.value })}
              className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-2 font-sans focus:outline-none"
            />
          </SmallField>
          <SmallField label="Title">
            <input
              data-testid={ADMIN.announcementTitleInput}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-2 font-sans focus:outline-none"
            />
          </SmallField>
          <SmallField label="Body">
            <textarea
              data-testid={ADMIN.announcementBodyInput}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={4}
              className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-2 font-sans focus:outline-none resize-none"
            />
          </SmallField>

          <button
            type="submit"
            disabled={submitting}
            data-testid={ADMIN.announcementSubmit}
            className="inline-flex items-center gap-2 bg-cocoa-900 text-parchment-100 px-6 py-3 text-sm font-sans hover:bg-terracotta transition-colors disabled:opacity-60"
          >
            <Send size={14} /> {submitting ? "Posting…" : "Post Announcement"}
          </button>
        </form>
      </div>

      <div className="lg:col-span-7">
        <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">
          Current
        </div>
        <h2 className="font-serif text-3xl text-cocoa-900 mb-8">
          {items.length} announcement{items.length === 1 ? "" : "s"}
        </h2>

        {items.length === 0 ? (
          <div className="border border-dashed border-parchment-400 p-16 text-center text-cocoa-700 font-sans">
            ✦ No announcements yet.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((a) => (
              <article key={a.id} className="bg-parchment-50 border border-parchment-400 p-6 flex gap-5 items-start">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-terracotta mb-2">
                    {a.badge}
                  </div>
                  <h3 className="font-serif text-xl text-cocoa-900 mb-2">{a.title}</h3>
                  <p className="font-sans text-cocoa-800 font-light leading-relaxed text-sm">
                    {a.body}
                  </p>
                </div>
                <button
                  data-testid={ADMIN.announcementDelete}
                  onClick={() => remove(a.id)}
                  className="text-cocoa-700 hover:text-terracotta transition-colors"
                  aria-label="Delete announcement"
                >
                  <Trash2 size={16} />
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConstructionTab() {
  const [data, setData] = useState({ percentage: 20, phase_label: "", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/construction").then((res) => setData(res.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        percentage: Number(data.percentage),
        phase_label: data.phase_label,
        note: data.note,
      };
      const res = await api.put("/construction", payload);
      setData(res.data);
      toast.success("Construction progress updated.");
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7">
        <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">
          Update
        </div>
        <h2 className="font-serif text-3xl text-cocoa-900 mb-8">Construction Progress</h2>

        <form onSubmit={save} className="bg-parchment-50 border border-parchment-400 p-8 space-y-6">
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-1">
              Percentage (0—100)
            </label>
            <input
              type="number"
              min={0}
              max={100}
              data-testid={ADMIN.constructionPercentInput}
              value={data.percentage}
              onChange={(e) => setData({ ...data, percentage: e.target.value })}
              className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-2 font-serif text-3xl text-cocoa-900 focus:outline-none"
            />
          </div>
          <SmallField label="Phase label">
            <input
              data-testid={ADMIN.constructionPhaseInput}
              value={data.phase_label}
              onChange={(e) => setData({ ...data, phase_label: e.target.value })}
              className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-2 font-sans focus:outline-none"
            />
          </SmallField>
          <SmallField label="Note (shown publicly)">
            <textarea
              data-testid={ADMIN.constructionNoteInput}
              value={data.note}
              onChange={(e) => setData({ ...data, note: e.target.value })}
              rows={3}
              className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-2 font-sans focus:outline-none resize-none"
            />
          </SmallField>

          <button
            type="submit"
            disabled={saving}
            data-testid={ADMIN.constructionSave}
            className="bg-cocoa-900 text-parchment-100 px-6 py-3 text-sm font-sans hover:bg-terracotta transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Progress"}
          </button>
        </form>
      </div>

      <div className="lg:col-span-5">
        <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">
          Preview
        </div>
        <h2 className="font-serif text-3xl text-cocoa-900 mb-8">Current</h2>
        <div className="bg-cocoa-900 text-parchment-100 p-8 border border-cocoa-900">
          <div className="font-serif text-7xl">
            {data.percentage}
            <span className="text-3xl text-gold">%</span>
          </div>
          <div className="mt-2 text-sm text-parchment-200/80 font-sans">
            {data.phase_label}
          </div>
          <div className="mt-6 h-1.5 bg-parchment-100/15 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-gold via-terracotta to-gold"
              style={{ width: `${Math.min(100, Math.max(0, data.percentage))}%` }}
            />
          </div>
          <p className="mt-6 italic text-parchment-200/70 text-sm">{data.note}</p>
        </div>
      </div>
    </div>
  );
}

function SmallField({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const APPT_TYPES = {
  pastoral_interview: "Pastoral Interview",
  holy_mass: "Holy Mass",
  baptism: "Baptism",
  wedding: "Wedding",
  anointing: "Anointing of the Sick",
  funeral: "Funeral Mass",
  blessing: "Blessing",
  other: "Other Concern",
};

function ParishReplyBlock({ appointment, onReplied, endpoint = "appointments", quickReplies }) {
  const [text, setText] = useState(appointment.parish_reply || "");
  const [by, setBy] = useState(appointment.parish_reply_by || "Parish Office");
  const [editing, setEditing] = useState(!appointment.parish_reply);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!text.trim()) return toast.error("Reply cannot be empty.");
    setSending(true);
    try {
      const res = await api.post(`/${endpoint}/${appointment.id}/reply`, {
        text: text.trim(),
        by: by.trim() || "Parish Office",
      });
      onReplied({
        parish_reply: res.data.parish_reply,
        parish_reply_by: res.data.parish_reply_by,
        parish_reply_at: res.data.parish_reply_at,
      });
      setEditing(false);
      toast.success("Reply sent. The requester can view it via the status lookup.");
    } catch {
      toast.error("Could not send reply.");
    } finally {
      setSending(false);
    }
  };

  const QUICK = quickReplies || [
    "Thank you for your request. Kindly come to the parish office on Monday — Saturday, 8:00 AM to 5:00 PM (closed Tuesdays and Sundays), and bring the required documents.",
    "We confirm your appointment on the date and time above. Please bring the required documents to the parish office during office hours: Monday — Saturday, 8:00 AM to 5:00 PM (closed Tuesdays and Sundays).",
    "We need to reschedule due to a conflicting parish activity. Please contact the parish office on Monday — Saturday, 8:00 AM to 5:00 PM (closed Tuesdays and Sundays).",
    "Kindly complete the required documents before we can confirm. The parish office is open Monday — Saturday, 8:00 AM to 5:00 PM (closed Tuesdays and Sundays).",
  ];

  return (
    <div className="mt-6 pt-5 border-t border-parchment-300">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta font-medium">
          ✉ Reply from the Parish
        </div>
        {appointment.parish_reply_at && (
          <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark">
            Last replied {new Date(appointment.parish_reply_at).toLocaleString()}
          </div>
        )}
      </div>

      {!editing && appointment.parish_reply ? (
        <div className="bg-parchment-200 border-l-2 border-terracotta p-4">
          <p className="font-serif italic text-cocoa-900 leading-relaxed whitespace-pre-wrap">
            &ldquo;{appointment.parish_reply}&rdquo;
          </p>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] uppercase tracking-[0.22em] text-cocoa-700">
              — {appointment.parish_reply_by || "Parish Office"}
            </div>
            <button
              data-testid="parish-reply-edit"
              onClick={() => setEditing(true)}
              className="text-[11px] uppercase tracking-[0.22em] text-terracotta hover:text-cocoa-900"
            >
              Edit / Resend
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <textarea
            data-testid="parish-reply-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="Write a pastoral reply to the requester…"
            className="w-full bg-parchment-100 border border-parchment-400 focus:border-terracotta focus:outline-none px-4 py-3 font-sans text-sm text-cocoa-900 resize-y"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <input
              data-testid="parish-reply-by"
              value={by}
              onChange={(e) => setBy(e.target.value)}
              placeholder="Signed by (e.g. Fr. Ariel D. Destora)"
              className="flex-1 min-w-[200px] bg-parchment-100 border border-parchment-400 focus:border-terracotta focus:outline-none px-4 py-2 font-sans text-sm text-cocoa-900"
            />
            <button
              type="button"
              data-testid="parish-reply-send"
              onClick={send}
              disabled={sending}
              className="inline-flex items-center gap-2 bg-cocoa-900 text-parchment-100 px-5 py-2 text-sm font-sans hover:bg-terracotta transition-colors disabled:opacity-60"
            >
              {sending ? "Sending…" : appointment.parish_reply ? "Update Reply" : "Send Reply"}
            </button>
            {appointment.parish_reply && (
              <button
                type="button"
                onClick={() => { setText(appointment.parish_reply); setEditing(false); }}
                className="text-sm text-cocoa-700 hover:text-terracotta"
              >
                Cancel
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {QUICK.map((q, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setText(q)}
                className="text-[10px] uppercase tracking-[0.22em] border border-parchment-400 px-3 py-1.5 hover:border-terracotta hover:text-terracotta text-cocoa-700"
              >
                Quick reply {idx + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AppointmentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/appointments");
      setItems(res.data);
    } catch {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (id, fields) => {
    try {
      await api.patch(`/appointments/${id}`, fields);
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...fields } : x)));
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const statuses = ["all", "pending", "confirmed", "rescheduled", "completed", "cancelled"];
  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  const statusColor = {
    pending: "text-terracotta",
    confirmed: "text-sage-dark",
    rescheduled: "text-gold",
    completed: "text-cocoa-700",
    cancelled: "text-cocoa-700/60",
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">
            Appointment Requests
          </div>
          <h2 className="font-serif text-3xl text-cocoa-900">
            {filtered.length} of {items.length}
          </h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-sans border transition-colors ${
                filter === s
                  ? "bg-cocoa-900 text-parchment-100 border-cocoa-900"
                  : "border-parchment-400 text-cocoa-800 hover:border-cocoa-900"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-cocoa-700 font-sans">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-parchment-400 p-16 text-center text-cocoa-700 font-sans">
          ✦ No appointments in this category.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((i) => (
            <article
              key={i.id}
              data-testid="admin-appointment-row"
              className="bg-parchment-50 border border-parchment-400 p-6 md:p-8"
            >
              <div className="grid md:grid-cols-12 gap-4 md:gap-6">
                <div className="md:col-span-3">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta font-medium">
                    {APPT_TYPES[i.type] || i.type}
                  </div>
                  <div className="font-serif text-xl text-cocoa-900 mt-1">{i.full_name}</div>
                  {i.contact && (
                    <a
                      href={`tel:${i.contact.replace(/\s/g, "")}`}
                      className="block text-sm text-cocoa-700 hover:text-terracotta"
                    >
                      {i.contact}
                    </a>
                  )}
                  {i.email && (
                    <a href={`mailto:${i.email}`} className="block text-sm text-cocoa-700 hover:text-terracotta break-all">
                      {i.email}
                    </a>
                  )}
                  {i.address && (
                    <div className="text-xs text-cocoa-700 mt-1 italic">{i.address}</div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mt-2">
                    Submitted {new Date(i.created_at).toLocaleString()}
                  </div>
                  <div className="text-[10px] font-mono text-cocoa-700/70 mt-1">
                    REF: {i.id.slice(0, 8).toUpperCase()}
                  </div>
                </div>

                <div className="md:col-span-6 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <SmallField label="Preferred Date">
                      <div className="font-serif text-cocoa-900">
                        {i.preferred_date || "—"}
                      </div>
                    </SmallField>
                    <SmallField label="Preferred Time">
                      <div className="font-serif text-cocoa-900">
                        {i.preferred_time || "—"}
                      </div>
                    </SmallField>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-1">Purpose</div>
                    <p className="text-sm text-cocoa-900 font-sans leading-relaxed whitespace-pre-wrap">
                      {i.purpose}
                    </p>
                  </div>
                  {i.notes && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-1">Notes</div>
                      <p className="text-sm text-cocoa-700 font-sans italic leading-relaxed whitespace-pre-wrap">
                        {i.notes}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-parchment-300">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-1">
                        Confirmed Date
                      </div>
                      <input
                        type="date"
                        value={i.confirmed_date || ""}
                        onChange={(e) => patch(i.id, { confirmed_date: e.target.value, status: e.target.value ? "confirmed" : i.status })}
                        className="bg-parchment-100 border border-parchment-400 px-3 py-1.5 text-sm font-sans text-cocoa-900 focus:border-terracotta focus:outline-none w-full"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-1">
                        Confirmed Time
                      </div>
                      <input
                        type="time"
                        value={i.confirmed_time || ""}
                        onChange={(e) => patch(i.id, { confirmed_time: e.target.value })}
                        className="bg-parchment-100 border border-parchment-400 px-3 py-1.5 text-sm font-sans text-cocoa-900 focus:border-terracotta focus:outline-none w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-3 flex md:flex-col gap-3 md:items-end justify-end">
                  <div className={`text-[11px] uppercase tracking-[0.28em] font-medium ${statusColor[i.status] || "text-cocoa-700"}`}>
                    ● {i.status}
                  </div>
                  <select
                    value={i.status}
                    onChange={(e) => patch(i.id, { status: e.target.value })}
                    className="bg-parchment-100 border border-parchment-400 px-3 py-2 text-sm font-sans text-cocoa-900 focus:border-terracotta focus:outline-none w-full"
                  >
                    {statuses.filter((s) => s !== "all").map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => remove(i.id)}
                    className="flex items-center gap-2 text-sm text-cocoa-700 hover:text-terracotta transition-colors"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>

              {/* Parish reply panel */}
              <ParishReplyBlock appointment={i} onReplied={(updated) => setItems((arr) => arr.map((x) => (x.id === i.id ? { ...x, ...updated } : x)))} />
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const TOMB_STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const TOMB_QUICK = [
  "Your tomb blessing is confirmed on the date and time above. Kindly wait at the tomb a few minutes before the schedule. Eternal rest grant unto them, O Lord.",
  "Due to the number of requests, the priest or lay cooperator may arrive a little later than the requested time. Thank you for your patience and prayers.",
  "We need to adjust your blessing schedule. Kindly call the parish office at 0915 581 1419 so we can agree on a new time.",
  "Thank you — your donation has been received. May the souls of the faithful departed rest in peace.",
];

function TombRow({ i, onPatch, onRemove, onReplied }) {
  return (
    <article data-testid="admin-tomb-row" className="bg-parchment-50 border border-parchment-400 p-6 md:p-8">
      <div className="grid md:grid-cols-12 gap-4 md:gap-6">
        <div className="md:col-span-4">
          <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta font-medium">{i.cemetery}</div>
          <div className="font-serif text-xl text-cocoa-900 mt-1">{i.requester_name}</div>
          <a href={`tel:${i.contact.replace(/\s/g, "")}`} className="block text-sm text-cocoa-700 hover:text-terracotta">{i.contact}</a>
          {i.email && <div className="text-sm text-cocoa-700 break-all">{i.email}</div>}
          <div className="text-[10px] font-mono text-cocoa-700/70 mt-2">REF: {i.id.slice(0, 8).toUpperCase()}</div>
        </div>
        <div className="md:col-span-5 grid grid-cols-2 gap-3">
          <SmallField label="Date & Time"><div className="font-serif text-cocoa-900">{fmtDate(i.blessing_date)} · {fmtTime(i.blessing_time)}</div></SmallField>
          <SmallField label="Donation"><div className="font-serif text-cocoa-900">{peso(i.donation_amount)}</div></SmallField>
          <div className="col-span-2">
            <SmallField label="Deceased"><div className="font-serif text-cocoa-900">{i.deceased_names.join(", ")}</div></SmallField>
          </div>
          {i.tomb_location && <div className="col-span-2 text-sm italic text-cocoa-700">Location: {i.tomb_location}</div>}
          {i.notes && <div className="col-span-2 text-sm italic text-cocoa-700 whitespace-pre-wrap">Notes: {i.notes}</div>}
        </div>
        <div className="md:col-span-3 flex md:flex-col gap-3 md:items-end justify-end">
          <select data-testid="admin-tomb-status" value={i.status} onChange={(e) => onPatch(i.id, e.target.value)} className="bg-parchment-100 border border-parchment-400 px-3 py-2 text-sm font-sans text-cocoa-900 w-full">
            {TOMB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="admin-tomb-delete" onClick={() => onRemove(i.id)} className="flex items-center gap-2 text-sm text-cocoa-700 hover:text-terracotta"><Trash2 size={14} /> Delete</button>
        </div>
      </div>
      <ParishReplyBlock appointment={i} endpoint="tomb-blessings" quickReplies={TOMB_QUICK} onReplied={onReplied} />
    </article>
  );
}

function TombBlessingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cem, setCem] = useState("all");

  useEffect(() => {
    api.get("/tomb-blessings").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load tomb blessings")).finally(() => setLoading(false));
  }, []);

  const merge = (id, fields) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...fields } : x)));
  const patch = async (id, status) => {
    try { await api.patch(`/tomb-blessings/${id}`, { status }); merge(id, { status }); } catch { toast.error("Could not update"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/tomb-blessings/${id}`); setItems((a) => a.filter((x) => x.id !== id)); toast.success("Deleted"); } catch { toast.error("Delete failed"); }
  };

  const filtered = cem === "all" ? items : items.filter((i) => i.cemetery === cem);
  const total = filtered.reduce((s, i) => s + (i.status !== "cancelled" ? i.donation_amount : 0), 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">Tomb Blessing Requests · Oct 31 – Nov 2</div>
          <h2 className="font-serif text-3xl text-cocoa-900">{filtered.length} requests · <span data-testid="admin-tomb-total">{peso(total)}</span> pledged</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", ...CEMETERIES].map((c) => (
            <button key={c} data-testid={`admin-tomb-filter-${c === "all" ? "all" : CEMETERIES.indexOf(c)}`} onClick={() => setCem(c)} className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border ${cem === c ? "bg-cocoa-900 text-parchment-100 border-cocoa-900" : "border-parchment-400 text-cocoa-800 hover:border-cocoa-900"}`}>{c}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="text-cocoa-700">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-parchment-400 p-16 text-center text-cocoa-700">✦ No tomb blessing requests yet.</div>
      ) : (
        <div className="space-y-4">
          {filtered.map((i) => (
            <TombRow key={i.id} i={i} onPatch={patch} onRemove={remove} onReplied={(u) => merge(i.id, u)} />
          ))}
        </div>
      )}
    </div>
  );
}

function IntentionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/intentions");
      setItems(res.data);
    } catch {
      toast.error("Failed to load intentions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/intentions/${id}`, { status });
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch {
      toast.error("Could not update");
    }
  };

  const updateScheduledDate = async (id, scheduled_date) => {
    try {
      const payload = scheduled_date ? { scheduled_date, status: "scheduled" } : { scheduled_date: "" };
      await api.patch(`/intentions/${id}`, payload);
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, scheduled_date, status: scheduled_date ? "scheduled" : i.status } : i)));
      toast.success(scheduled_date ? "Mass date set." : "Date cleared.");
    } catch {
      toast.error("Could not update");
    }
  };

  const togglePublic = async (id, is_public) => {
    try {
      await api.patch(`/intentions/${id}`, { is_public });
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, is_public } : i)));
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this intention?")) return;
    try {
      await api.delete(`/intentions/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const types = ["all", "celebration", "petition", "thanksgiving", "repose"];
  const filtered = filter === "all" ? items : items.filter((i) => i.type === filter);

  const typeColor = {
    celebration: "text-gold",
    petition: "text-terracotta",
    thanksgiving: "text-sage-dark",
    repose: "text-cocoa-700",
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">
            Mass Intentions
          </div>
          <h2 className="font-serif text-3xl text-cocoa-900">
            {filtered.length} of {items.length}
          </h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`intention-filter-${t}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-sans border transition-colors ${
                filter === t
                  ? "bg-cocoa-900 text-parchment-100 border-cocoa-900"
                  : "border-parchment-400 text-cocoa-800 hover:border-cocoa-900"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-cocoa-700 font-sans">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-parchment-400 p-16 text-center text-cocoa-700 font-sans">
          ✦ No intentions in this category.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((i) => (
            <article
              key={i.id}
              data-testid="admin-intention-row"
              className="bg-parchment-50 border border-parchment-400 p-6 md:p-8"
            >
              <div className="grid md:grid-cols-12 gap-4 md:gap-6">
                <div className="md:col-span-3">
                  <div className={`text-[10px] uppercase tracking-[0.28em] font-medium ${typeColor[i.type] || "text-cocoa-700"}`}>
                    {i.type}{i.occasion ? ` · ${i.occasion}` : ""}
                  </div>
                  <div className="font-serif text-lg text-cocoa-900 mt-1">{i.requested_by}</div>
                  {i.email && (
                    <a href={`mailto:${i.email}`} className="block text-sm text-cocoa-700 hover:text-terracotta">
                      {i.email}
                    </a>
                  )}
                  {i.contact && (
                    <a href={`tel:${i.contact.replace(/\s/g, "")}`} className="block text-sm text-cocoa-700 hover:text-terracotta">
                      {i.contact}
                    </a>
                  )}
                  {i.preferred_date && (
                    <div className="text-[11px] uppercase tracking-[0.22em] text-terracotta mt-2 font-sans">
                      Preferred: {i.preferred_date}
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mt-2">
                    {new Date(i.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="md:col-span-6">
                  <p className="font-serif italic text-cocoa-900 leading-relaxed text-[16px] whitespace-pre-wrap">
                    &ldquo;{i.intention}&rdquo;
                  </p>
                </div>

                <div className="md:col-span-3 flex md:flex-col gap-3 md:items-end justify-end">
                  <input
                    type="date"
                    value={i.scheduled_date || ""}
                    onChange={(e) => updateScheduledDate(i.id, e.target.value)}
                    data-testid="admin-intention-scheduled-date"
                    title="Set the Mass date — also publishes as ‘Scheduled’"
                    className="bg-parchment-100 border border-parchment-400 px-3 py-2 text-sm font-sans text-cocoa-900 focus:border-terracotta focus:outline-none w-full md:w-44"
                  />
                  <select
                    data-testid="admin-intention-status"
                    value={i.status}
                    onChange={(e) => updateStatus(i.id, e.target.value)}
                    className="bg-parchment-100 border border-parchment-400 px-3 py-2 text-sm font-sans text-cocoa-900 focus:border-terracotta focus:outline-none w-full md:w-44"
                  >
                    <option value="new">New</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="offered">Offered</option>
                  </select>
                  <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cocoa-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={i.is_public !== false}
                      onChange={(e) => togglePublic(i.id, e.target.checked)}
                      data-testid="admin-intention-public-toggle"
                      className="accent-terracotta"
                    />
                    Show publicly
                  </label>
                  <button
                    data-testid="admin-intention-delete"
                    onClick={() => remove(i.id)}
                    className="flex items-center gap-2 text-sm text-cocoa-700 hover:text-terracotta transition-colors"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function PledgesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/pledges");
      setItems(res.data);
    } catch {
      toast.error("Failed to load pledges");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/pledges/${id}`, { status });
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this pledge?")) return;
    try {
      await api.delete(`/pledges/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const fmtPeso = (n) =>
    n === null || n === undefined
      ? ""
      : new Intl.NumberFormat("en-PH", { minimumFractionDigits: 2 }).format(Number(n));

  const totalPledged = items
    .filter((i) => i.status !== "rejected")
    .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2">
            Digital Pledges
          </div>
          <h2 className="font-serif text-3xl text-cocoa-900">
            {items.length} pledge{items.length === 1 ? "" : "s"}
          </h2>
        </div>
        <div className="bg-cocoa-900 text-parchment-100 px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold mb-1">
            Total Pledged
          </div>
          <div className="font-serif text-3xl text-gold">₱ {fmtPeso(totalPledged)}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-cocoa-700 font-sans">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-parchment-400 p-16 text-center text-cocoa-700 font-sans">
          ✦ No digital pledges yet.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((i) => (
            <article
              key={i.id}
              data-testid="admin-pledge-row"
              className="bg-parchment-50 border border-parchment-400 p-6 md:p-8"
            >
              <div className="grid md:grid-cols-12 gap-4 md:gap-6">
                <div className="md:col-span-3">
                  <div className="font-serif text-xl text-cocoa-900">{i.name}</div>
                  {i.email && (
                    <a
                      href={`mailto:${i.email}`}
                      className="block text-sm text-cocoa-700 hover:text-terracotta"
                    >
                      {i.email}
                    </a>
                  )}
                  {i.contact && (
                    <a
                      href={`tel:${i.contact.replace(/\s/g, "")}`}
                      className="block text-sm text-cocoa-700 hover:text-terracotta"
                    >
                      {i.contact}
                    </a>
                  )}
                  {i.address && (
                    <div className="text-xs text-cocoa-700 mt-1 italic">{i.address}</div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mt-2">
                    {new Date(i.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="md:col-span-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark">
                        Level
                      </div>
                      <div className="font-serif text-2xl text-terracotta">
                        {i.level_label || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark">
                        Amount
                      </div>
                      <div className="font-serif text-2xl text-cocoa-900">
                        ₱ {fmtPeso(i.amount)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark">
                        Payment
                      </div>
                      <div className="font-sans text-cocoa-900 text-sm">
                        {i.payment || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark">
                        Form No.
                      </div>
                      <div className="font-mono text-cocoa-900 text-sm">{i.no || "—"}</div>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-3 flex md:flex-col gap-3 md:items-end justify-end">
                  <select
                    data-testid="admin-pledge-status"
                    value={i.status}
                    onChange={(e) => updateStatus(i.id, e.target.value)}
                    className="bg-parchment-100 border border-parchment-400 px-3 py-2 text-sm font-sans text-cocoa-900 focus:border-terracotta focus:outline-none"
                  >
                    <option value="new">New</option>
                    <option value="acknowledged">Acknowledged</option>
                    <option value="received">Received</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <button
                    data-testid="admin-pledge-delete"
                    onClick={() => remove(i.id)}
                    className="flex items-center gap-2 text-sm text-cocoa-700 hover:text-terracotta transition-colors"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
